import { Suspense, useEffect, useRef, useState } from 'react';
import { Outlet, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useIsRestoring, useQueryClient } from '@tanstack/react-query';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { Toaster } from '@/components/ui/sonner';
import { PwaUpdatePrompt } from '@/components/offline/PwaUpdatePrompt';
import { OfflineBanner } from '@/components/offline/OfflineBanner';
import { OfflineScreen } from '@/components/offline/OfflineScreen';
import { TooltipProvider } from '@/components/ui/tooltip';
import { login, initAuth, isMasterAdmin, isBrowserOffline, useOnlineStatus, useToastStore, API, useAuthStore, useSchool, logout, selectOfflineUserId, clearPersistedQueryCache } from '@sportschool/shared-logic';
import { useMobilePushTokenSync } from '../hooks/useMobilePushTokenSync';
import { useAppBadgeSync } from '../hooks/useAppBadgeSync';
import { useOfflinePrefetch } from '../hooks/useOfflinePrefetch';
import { SideNavBar } from './SideNavBar';
import { TopAppBar } from './TopAppBar';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BottomNavBar } from './BottomNavBar';
import { ErrorBoundary } from './ErrorBoundary';
import { JoinSchoolView } from './JoinSchoolView';
import { toast as sonnerToast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { SchoolProvider } from '@/context/SchoolContext';
import { cn } from '@/lib/utils';
import logoBlue from '../images/B-Blue.svg';

function useToastListener() {
  const { t } = useTranslation();
  const toasts = useToastStore((state) => state.toasts);
  const removeToast = useToastStore((state) => state.removeToast);

  useEffect(() => {
    if (toasts.length === 0) return;

    toasts.forEach((tItem) => {
      const message = tItem.code
        ? t(tItem.code.includes('.') ? tItem.code : `errors.${tItem.code}`, { ...tItem.args, defaultValue: tItem.message })
        : tItem.message;

      if (tItem.variant === 'success') {
        sonnerToast.success(message);
      } else if (tItem.variant === 'error') {
        sonnerToast.error(message, { duration: 6000 });
      } else if (tItem.variant === 'warning') {
        sonnerToast.warning(message, { duration: 5000 });
      } else {
        sonnerToast(message);
      }
      removeToast(tItem.id);
    });
  }, [toasts, removeToast, t]);
}

function PageLoader() {
  return <LoadingSpinner size="md" />;
}

export function Layout() {
  useToastListener();
  const queryClient = useQueryClient();
  const { schoolId } = useParams<{ schoolId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  // Signed-in user (`sub` of the OIDC session); the user context must belong to them (or to the impersonated user).
  const [authUserId, setAuthUserId] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Offline cold start without a stored session: the login page cannot load, so wait for the connection.
  const [isOfflineWithoutUser, setIsOfflineWithoutUser] = useState(false);
  const isRestoringCache = useIsRestoring();
  const { browserOnline } = useOnlineStatus();

  const activeSchoolId = schoolId || localStorage.getItem('active_school_id') || '';
  useMobilePushTokenSync(isAuthChecked, activeSchoolId);
  useAppBadgeSync(activeSchoolId);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);
  const setUserContext = useAuthStore((s) => s.setUserContext);
  const activeRole = useAuthStore((s) => s.activeRole);
  const setActiveRole = useAuthStore((s) => s.setActiveRole);
  const impersonationToken = useAuthStore((s) => s.impersonationToken);
  const stopImpersonation = useAuthStore((s) => s.stopImpersonation);
  const impersonatedUser = useAuthStore((s) => s.userContext?.user);

  const handleStopImpersonation = async () => {
    const returnPath = useAuthStore.getState().originalPath;
    stopImpersonation();
    queryClient.clear();
    // The reload must not restore the impersonated user's cached data (e.g. their user context and roles)
    await clearPersistedQueryCache();
    const destination = returnPath || `/schools/${activeSchoolId}/dashboard`;
    window.location.href = destination;
  };

  const getRoleLabel = (role: string | null) => {
    if (role === 'Student') return 'Zawodnik';
    if (role === 'Coach') return 'Trener';
    if (role === 'Admin') return 'Administrator';
    return role || '';
  };

  const { data: contextData, error: contextError, refetch: refetchContext } = API.useGetUserContext(activeSchoolId, {
    query: {
      enabled: !!activeSchoolId && isAuthChecked,
      retry: false,
    }
  });

  const { data: school, error: schoolError } = useSchool(isAuthChecked ? activeSchoolId : '');

  const schoolStatus = (schoolError as { response?: { status?: number } } | null)?.response?.status;
  const isSchoolInvalid = schoolStatus === 403 || schoolStatus === 404;

  useEffect(() => {
    if (school?.name) {
      document.title = school.name;
    } else {
      document.title = 'Badmintomania!';
    }
    return () => {
      document.title = 'Badmintomania!';
    };
  }, [school]);

  const { t, i18n } = useTranslation();
  useEffect(() => {
    if (i18n.language) {
      document.documentElement.lang = i18n.language;
    }
  }, [i18n.language]);

  useEffect(() => {
    if (isSchoolInvalid && activeSchoolId) {
      if (localStorage.getItem('active_school_id') === activeSchoolId) {
        localStorage.removeItem('active_school_id');
      }
      navigate('/select-school', { replace: true });
    }
  }, [isSchoolInvalid, activeSchoolId, navigate]);

  const refetchedForUserRef = useRef<string | null>(null);
  useEffect(() => {
    if (!contextData) return;

    // A cached context of another user (e.g. the impersonated student right after "Return to admin") must not
    // replace the signed-in user's context nor pick their role: refetch it (once per user) and wait for the result.
    const expectedUserId = impersonationToken ? selectOfflineUserId(useAuthStore.getState()) : authUserId;
    if (!expectedUserId) return;
    if (contextData.user?.id !== expectedUserId) {
      if (refetchedForUserRef.current !== expectedUserId) {
        refetchedForUserRef.current = expectedUserId;
        void refetchContext();
      }
      return;
    }

    setUserContext(contextData);

    const roles = contextData.activeSchool?.roles || [];

    // Initialize or validate activeRole
    let currentRole = activeRole;
    if (!currentRole || !roles.includes(currentRole)) {
      if (roles.includes('Admin')) {
        currentRole = 'Admin';
      } else if (roles.includes('Coach')) {
        currentRole = 'Coach';
      } else if (roles.includes('Student')) {
        currentRole = 'Student';
      } else {
        currentRole = null;
      }
      setActiveRole(currentRole);
    }
  }, [contextData, setUserContext, activeRole, setActiveRole, impersonationToken, authUserId, refetchContext]);

  useEffect(() => {
    if (!activeSchoolId || !isAuthChecked || !contextData || !activeRole) return;

    const path = location.pathname;

    // Route protections based on activeRole
    if (activeRole === 'Student') {
      // Students should not be on admin/coach pages.
      const isAdminOrCoachPage = 
        path.endsWith('/dashboard') ||
        path.includes('/schedules') ||
        path.includes('/sessions') ||
        path.includes('/students') ||
        path.includes('/coaches') ||
        path.includes('/pricelists') ||
        path.includes('/calendar') ||
        path.includes('/coupons') ||
        path.includes('/makeup-credits') ||
        path.includes('/tournaments') ||
        path.endsWith('/coach');

      if (isAdminOrCoachPage) {
        navigate(`/schools/${activeSchoolId}/student`, { replace: true });
      }
    } else if (activeRole === 'Coach') {
      // Coaches should not be on admin or student portal pages
      const myMemberId = contextData?.activeSchool?.memberId;
      const isMyCoachPage = myMemberId ? path.includes(`/coaches/${myMemberId}`) : false;

      const isRestrictedForCoach =
        path.includes('/locations') ||
        path.includes('/calendar') ||
        path.includes('/schedules') ||
        path.includes('/pricelists') ||
        path.includes('/students') ||
        (path.includes('/coaches') && !isMyCoachPage) ||
        path.endsWith('/sessions') ||
        path.endsWith('/student') ||
        path.includes('/classes') ||
        path.includes('/payments') ||
        path.includes('/coupons') ||
        path.includes('/makeup-credits');

      if (isRestrictedForCoach) {
        navigate(`/schools/${activeSchoolId}/dashboard`, { replace: true });
      }
    } else if (activeRole === 'Admin') {
      // Admins should not be on student or coach specific portals directly
      const isRestrictedForAdmin =
        path.endsWith('/student') ||
        path.includes('/classes') ||
        path.endsWith('/coach') ||
        path.includes('/coach/past-sessions');
      if (isRestrictedForAdmin) {
        navigate(`/schools/${activeSchoolId}/dashboard`, { replace: true });
      }
    }
  }, [activeRole, location.pathname, activeSchoolId, isAuthChecked, contextData, navigate]);

  useEffect(() => {
    let cancelled = false;

    const checkAuth = async () => {
      const user = await initAuth();

      if (!user) {
        // Offline without a stored session: the KeyCloak login page cannot load — retry once back online.
        if (isBrowserOffline()) {
          setIsOfflineWithoutUser(true);
          window.addEventListener('online', retryWhenOnline, { once: true });
          return;
        }
        setIsOfflineWithoutUser(false);
        localStorage.setItem('auth_return_url', location.pathname + location.search);
        await login();
        return;
      }

      if (cancelled) return;

      setAuthUserId(user.profile?.sub ?? null);
      const isMaster = isMasterAdmin(user.access_token);

      // Guard the master-admin section
      if (location.pathname === '/admin' && !isMaster) {
        navigate('/', { replace: true });
        return;
      }

      // If they land on the root '/', redirect to the correct home view
      if (location.pathname === '/') {
        if (isMaster) {
          navigate('/admin', { replace: true });
          return;
        }

        const stored = localStorage.getItem('active_school_id');
        if (stored) {
          navigate(`/schools/${stored}/dashboard`, { replace: true });
          return;
        } else {
          try {
            const res = await API.listMySchools({ skipAuthRedirect: true });
            const schools = res as unknown as { id: string }[];
            if (schools.length === 0) {
              navigate('/select-school', { replace: true });
              return;
            }
            if (schools.length > 1) {
              navigate('/select-school', { replace: true });
              return;
            }
            localStorage.setItem('active_school_id', schools[0].id);
            navigate(`/schools/${schools[0].id}/dashboard`, { replace: true });
            return;
          } catch {
            navigate('/select-school', { replace: true });
            return;
          }
        }
      }

      // If they are on a school-scoped route, ensure it's saved as the active school
      if (schoolId && schoolId !== 'demo') {
        localStorage.setItem('active_school_id', schoolId);
      }

      setIsAuthChecked(true);
    };

    const retryWhenOnline = () => {
      if (!cancelled) {
        setIsOfflineWithoutUser(false);
        void checkAuth();
      }
    };

    checkAuth();
    return () => {
      cancelled = true;
      window.removeEventListener('online', retryWhenOnline);
    };
  }, [schoolId, navigate, location.pathname, location.search]);

  const isContextLoading = !!activeSchoolId && !contextData && !contextError;
  const isSchoolLoading = !!activeSchoolId && !school && !schoolError;
  const isLoading = !isAuthChecked || isContextLoading || isSchoolLoading;

  // Warm the offline cache (coach: next 7 days of sessions, student: enrollments + upcoming sessions).
  useOfflinePrefetch(activeSchoolId, isAuthChecked && !!contextData);

  // Offline and nothing cached to render the app from (no stored user, or school / user context never loaded).
  const isOfflineWithoutData =
    !browserOnline && !isRestoringCache && (isOfflineWithoutUser || (isAuthChecked && isLoading));

  const [showSplash, setShowSplash] = useState(true);
  const [isExiting, setIsExiting] = useState(false);
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setMinTimeElapsed(true);
    }, 1500);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (!isLoading && minTimeElapsed && showSplash) {
      setIsExiting(true);
      const timer = setTimeout(() => {
        setShowSplash(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isLoading, minTimeElapsed, showSplash]);

  if (isOfflineWithoutData) {
    return <OfflineScreen />;
  }

  if (showSplash && isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background">
        <img
          src={logoBlue}
          alt="Loading..."
          className="size-24 animate-pulse"
        />
      </div>
    );
  }

  if (isSchoolInvalid && activeSchoolId) {
    return (
      <div className="min-h-screen w-full flex flex-col items-center justify-center bg-background p-4 relative overflow-hidden">
        {/* Background Harmonious Gradients */}
        <div className="absolute inset-0 bg-gradient-to-tr from-primary/5 via-transparent to-primary/5 pointer-events-none" />
        <div className="absolute -top-[40%] -right-[30%] w-[80%] h-[80%] rounded-full bg-primary/5 blur-[120px] pointer-events-none" />
        <div className="absolute -bottom-[40%] -left-[30%] w-[80%] h-[80%] rounded-full bg-primary/5 blur-[120px] pointer-events-none" />

        <div className="w-full max-w-md z-10 flex flex-col items-center gap-6 text-center">
          <div className="w-16 h-16 rounded-2xl bg-destructive/10 flex items-center justify-center text-destructive mb-2 shadow-md">
            <span className="text-2xl font-extrabold">404</span>
          </div>
          <h1 className="text-2xl font-extrabold text-foreground tracking-tight">
            {t("layout.schoolNotFoundTitle")}
          </h1>
          <p className="text-sm text-muted-foreground max-w-sm">
            {t("layout.schoolNotFoundDesc")}
          </p>
          <div className="flex flex-col gap-3 w-full mt-4">
            <button
              onClick={() => {
                localStorage.removeItem('active_school_id');
                navigate('/select-school');
              }}
              className="inline-flex items-center justify-center rounded-xl text-sm font-bold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring bg-primary text-primary-foreground shadow-lg hover:shadow-xl shadow-primary/20 hover:shadow-primary/30 hover:bg-primary/90 h-11 px-6 w-full"
            >
              {t("layout.selectAnotherSchool")}
            </button>
            <button
              onClick={() => logout()}
              className="inline-flex items-center justify-center rounded-xl text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring border border-muted-foreground/15 bg-card/65 backdrop-blur-md hover:bg-muted/70 text-foreground h-11 px-6 w-full"
            >
              Wyloguj się
            </button>
          </div>
        </div>
      </div>
    );
  }

  const contextStatus = (contextError as { response?: { status?: number } } | null)?.response?.status;
  const isNotMember = contextStatus === 403 || contextStatus === 404;
  if (isNotMember && activeSchoolId) {
    return <JoinSchoolView schoolId={activeSchoolId} />;
  }

  return (
    <TooltipProvider>
      <SchoolProvider>
        <div className="flex h-screen w-full max-w-full bg-background relative overflow-x-hidden overflow-y-hidden">
        <SideNavBar schoolId={schoolId} />

        {/* Mobile Menu Overlay for Admin */}
        {activeRole === 'Admin' && (
          <>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="fixed top-2.5 left-6 z-[70] md:hidden cursor-pointer text-muted-foreground hover:text-foreground size-9 flex items-center justify-center rounded-lg hover:bg-muted"
            >
              {isMobileMenuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
            </Button>

            {isMobileMenuOpen && (
              <div className="fixed inset-0 z-[60] flex md:hidden">
                {/* Backdrop */}
                <div 
                  className="fixed inset-0 bg-background/80 backdrop-blur-sm animate-in fade-in duration-300"
                  onClick={() => setIsMobileMenuOpen(false)}
                />
                {/* Drawer Panel */}
                <div className="relative flex flex-col w-64 max-w-xs bg-card h-full border-r animate-in slide-in-from-left duration-300">
                  <SideNavBar 
                    schoolId={schoolId} 
                    isMobile 
                    onClose={() => setIsMobileMenuOpen(false)} 
                  />
                </div>
              </div>
            )}
          </>
        )}

        <div className="flex-1 flex flex-col min-w-0 max-w-full overflow-x-hidden">
          {!!impersonationToken && (
            <div className="bg-gradient-to-r from-amber-600 to-red-600 text-white px-4 py-2.5 flex items-center justify-between text-sm font-semibold shadow-md shrink-0 z-30">
              <div className="flex items-center gap-2">
                <span className="bg-white/20 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-extrabold">{t('impersonation.bannerTitle')}</span>
                <span>
                  {t('impersonation.actingAs')} <strong className="underline">{[impersonatedUser?.firstName, impersonatedUser?.lastName].filter(Boolean).join(' ') || impersonatedUser?.email || '—'}</strong> ({getRoleLabel(activeRole)})
                </span>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleStopImpersonation}
                className="bg-white text-warning hover:bg-white/90 border-none font-bold text-xs h-7 px-3 rounded-md shrink-0 cursor-pointer shadow-sm"
              >
                {t('impersonation.stop')}
              </Button>
            </div>
          )}
          <OfflineBanner />
          <TopAppBar />
          <main className="flex-1 p-4 md:p-8 pb-24 md:pb-8 overflow-x-hidden overflow-y-auto">
            <div className={cn(
              activeRole === 'Admin' || location.pathname.startsWith('/admin') ? "w-full" : "max-w-5xl mx-auto"
            )}>
              <ErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <Outlet />
                </Suspense>
              </ErrorBoundary>
            </div>
          </main>
          <BottomNavBar schoolId={schoolId} />
        </div>
      </div>
      <Toaster />
      <PwaUpdatePrompt />

      {/* Initial splash screen overlay with fade-out & zoom exit transition */}
      {showSplash && (
        <div className={cn(
          "fixed inset-0 z-50 flex items-center justify-center bg-background transition-opacity duration-500 ease-in-out",
          isExiting ? "opacity-0 pointer-events-none" : "opacity-100"
        )}>
          <img
            src={logoBlue}
            alt="Logo"
            className={cn(
              "size-24 transition-all duration-500 ease-in-out",
              isExiting ? "scale-75 opacity-0" : "animate-pulse scale-100"
            )}
          />
        </div>
      )}
      </SchoolProvider>
    </TooltipProvider>
  );
}
